import { DOCUMENT } from '@angular/common';
import {
  Component, ElementRef, Inject, OnInit, Optional, Renderer2, ViewChild,
} from '@angular/core';
import {
  FormBuilder, FormGroup, Validators, ReactiveFormsModule,
} from '@angular/forms';
import { Router } from '@angular/router';
import { chevronLeftIcon, chevronRightIcon, UIKitIconRegistry } from '@ui-kit';
import { asapScheduler } from 'rxjs';
import { AppService } from '../../app.service';
import { UserService } from '../../shared/service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-book-a-call',
  templateUrl: './book-a-call.component.html',
  styleUrls: ['./book-a-call.component.scss'],
})
export class BookACallComponent implements OnInit {
  @ViewChild('bookCallContainer', { static: true }) bookCallContainer: ElementRef;

  static TIMELINE_QUESTION = 'Firstly, when are you looking to buy?';

  timelineQuestion = BookACallComponent.TIMELINE_QUESTION;

  static TIMELINE_OPTIONS = [
    { label: 'Please select option', value: '0,-1', store: null },
    { label: '0 - 6 months', value: '1,2', store: 'Now to 6 months' },
    { label: '6 - 12 months', value: '2,1', store: '6 to 12 months' },
    { label: 'More than 1 year', value: '3,0', store: 'More than 1 year' },
  ];

  timelineOptions = BookACallComponent.TIMELINE_OPTIONS;

  static DEPOSIT_QUESTION = 'How much do you have in savings?';

  depositQuestion = BookACallComponent.DEPOSIT_QUESTION;

  static DEPOSIT_OPTIONS = [
    { label: 'Please select option', value: '0,-1', store: null },
    { label: 'Under £15K', value: '1,0', store: 'Under £15K' },
    { label: 'Between £15K - £50K', value: '2,1', store: 'Between £15K - £50K' },
    { label: 'Over £50K', value: '3,1', store: 'Over £50K' },
  ];

  depositOptions = BookACallComponent.DEPOSIT_OPTIONS;

  static HOME_QUESTION = 'Have you found a home you want to buy?';

  homeQuestion = BookACallComponent.HOME_QUESTION;

  static HOME_OPTIONS = [
    { label: 'Please select option', value: '0,-1', store: null },
    { label: 'Yes, I made or want to make an offer', value: '1,2', store: 'Yes' },
    { label: 'I\'m actively looking at homes', value: '2,1', store: 'No, but actively looking' },
    { label: 'Not started looking', value: '3,0', store: 'No' },
  ];

  homeOptions = BookACallComponent.HOME_OPTIONS;

  bookACallForm: FormGroup = this.formBuilder.group({
    timeline: [{ value: BookACallComponent.TIMELINE_OPTIONS[0].value, disabled: false }, Validators.compose([])],
    deposit: [{ value: BookACallComponent.DEPOSIT_OPTIONS[0].value, disabled: false }, Validators.compose([])],
    home: [{ value: BookACallComponent.HOME_OPTIONS[0].value, disabled: false }, Validators.compose([])],
  });

  bookACallFormStep: 'timeline' | 'deposit' | 'home' | 'book-a-call' | 'call-us' = 'timeline';

  removeTags: HTMLElement[] = [];

  constructor(
    @Optional() @Inject(DOCUMENT) private document: Document,
    private formBuilder: FormBuilder,
    private renderer: Renderer2,
    private router: Router,
    private user: UserService,
    private app: AppService,
    iconRegistry: UIKitIconRegistry,
  ) {
    iconRegistry.registerIcons([
      chevronLeftIcon,
      chevronRightIcon,
    ]);
  }

  ngOnInit(): void {
    const { qualifier }: any = this.user.current?.application || {};

    const timelineIndex = this.timelineOptions.map((option) => option.store).indexOf(qualifier?.timeline?.answer);
    const timeline = this.timelineOptions[timelineIndex]?.value || null;

    const depositIndex = this.depositOptions.map((option) => option.store).indexOf(qualifier?.deposit?.answer);
    const deposit = this.depositOptions[depositIndex]?.value || null;

    const homeIndex = this.homeOptions.map((option) => option.store).indexOf(qualifier?.home?.answer);
    const home = this.homeOptions[homeIndex]?.value || null;

    this.bookACallForm.setValue({
      timeline,
      deposit,
      home,
    });

    if (this.user.qualifiesForBookACall) {
      this.bookACallFormStep = 'book-a-call';
    }
  }

  onBookACallStep(step: string): void {
    const qualifier: any = this.user.current?.application?.qualifier || {};
    qualifier.createdAt = new Date().toISOString();

    const value = this.bookACallForm.value;

    switch (step) {
      case 'timeline':
        const timelineIndex = this.timelineOptions.map((option) => option.value).indexOf(value.timeline);
        const timeline = this.timelineOptions[timelineIndex]?.store;
        qualifier.timeline = {
          question: this.timelineQuestion,
          answer: timeline,
        };
        //
        this.bookACallFormStep = 'deposit';
        break;

      case 'deposit':
        const depositIndex = this.depositOptions.map((option) => option.value).indexOf(value.deposit);
        const deposit = this.depositOptions[depositIndex]?.store;
        qualifier.deposit = {
          question: this.depositQuestion,
          answer: deposit,
        };
        this.bookACallFormStep = 'home';
        break;

      case 'home':
        const homeIndex = this.homeOptions.map((option) => option.value).indexOf(value.home);
        const home = this.homeOptions[homeIndex]?.store;
        qualifier.home = {
          question: this.homeQuestion,
          answer: home,
        };
        // score
        const score = Object
          .keys(value)
          .map((key) => +value[key].split(',')[1])
          .reduce((total, v) => total + v, 0);
        // book a call or call us
        this.bookACallFormStep = score > 3
          ? 'book-a-call'
          : 'call-us';
        break;

      default:
        this.bookACallFormStep = 'timeline';
        break;
    }

    this.user
      .updateApplication({ qualifier })
      .toPromise();

    asapScheduler
      .schedule(() => this.renderer.setProperty(this.bookCallContainer?.nativeElement, 'scrollTop', 0), 300);
  }

  onBookACallClose(event?: MouseEvent | TouchEvent): void {
    if (window.parent) {
      window.parent.postMessage('close-booked-a-call', environment.urls.www);
    }
    asapScheduler
      .schedule(() => this.router.navigate(['/']), 300);
  }

  onBookACallEvent(event: any): void {
    switch (event?.type) {
      case 'booked-a-call':
        if (typeof (window as any)?.pintrk === 'function') {
          const script: HTMLScriptElement = this.renderer.createElement('script');
          const inlineScript = this.renderer.createText('pintrk(\'track\', \'lead\')');
          this.renderer.appendChild(script, inlineScript);
          this.renderer.appendChild(this.document.body, script);
          //
          const noScript: HTMLElement = this.renderer.createElement('noscript');
          const img: HTMLImageElement = this.renderer.createElement('img');
          img.width = 1;
          img.height = 1;
          this.renderer.setStyle(img, 'display', 'none');
          img.src = 'https://ct.pinterest.com/v3/?tid=2614481749724&event=lead&noscript=1';
          this.renderer.appendChild(noScript, img);
          this.renderer.appendChild(this.document.body, noScript);
          this.removeTags.push(script, noScript);
        }
        break;
      case 'close-booked-a-call':
        this.onBookACallClose();
        break;
      default:
        break;
    }

    asapScheduler
      .schedule(() => this.renderer.setProperty(this.bookCallContainer?.nativeElement, 'scrollTop', 0), 300);
  }
}
